import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { httpsCallable } from 'firebase/functions'
import { functions } from '@/lib/firebase'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Eye, Calendar, Trash2, User, EyeOff, Merge, Clock, Archive, RotateCcw } from 'lucide-react'
import { formatDateTime, formatRelativeTime } from '@/lib/dateTime'
import { MergePersonDialog } from '@/components/MergePersonDialog'
import type { Person, Classification, FaceEvent } from '@/types'

interface PersonDetailDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  personId: string | null
  initialPerson?: Person | null
  groupId: string
  onUpdated: () => void
  onDeleted: () => void
  onNavigateToSightings?: (personId: string, sightingId?: string, personName?: string) => void
  canManagePersons?: boolean
  isAdmin?: boolean
  isArchiveView?: boolean
}

export function PersonDetailDialog({
  open,
  onOpenChange,
  personId,
  initialPerson,
  groupId,
  onUpdated,
  onDeleted,
  canManagePersons = true,
  isAdmin = false,
  isArchiveView = false,
}: PersonDetailDialogProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [person, setPerson] = useState<Person | null>(null)
  const [classifications, setClassifications] = useState<Classification[]>([])
  const [recentEvents, setRecentEvents] = useState<FaceEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [archiving, setArchiving] = useState(false)
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Form state
  const [name, setName] = useState('')
  const [classificationId, setClassificationId] = useState('')
  const [notes, setNotes] = useState('')
  const [expiresAt, setExpiresAt] = useState('')

  const loadData = useCallback(async () => {
    if (!personId || !groupId) return

    setLoading(true)
    setError(null)

    try {
      // Load classifications
      const getClassificationsFn = httpsCallable(functions, 'getClassifications')
      const classResult = await getClassificationsFn({ groupId })
      const classData = classResult.data as { success: boolean; classifications: Classification[] }

      if (classData.success) {
        setClassifications(classData.classifications)
      }

      // Use initialPerson if provided, otherwise fetch from backend
      if (initialPerson) {
        setPerson(initialPerson)
        setName(initialPerson.name || '')
        setClassificationId(initialPerson.classificationId)
        setNotes(initialPerson.notes || '')
        setExpiresAt(initialPerson.expiresAt ? initialPerson.expiresAt.split('T')[0] : '')
      } else {
        // Fetch person by ID from backend
        const getPersonsFn = httpsCallable(functions, 'getPersons')
        const personsResult = await getPersonsFn({ groupId, personId })
        const personsData = personsResult.data as { success: boolean; persons: Person[] }

        if (personsData.success && personsData.persons.length > 0) {
          const foundPerson = personsData.persons[0]
          setPerson(foundPerson)
          setName(foundPerson.name || '')
          setClassificationId(foundPerson.classificationId)
          setNotes(foundPerson.notes || '')
          setExpiresAt(foundPerson.expiresAt ? foundPerson.expiresAt.split('T')[0] : '')
        } else {
          setError(t('persons.personNotFound'))
        }
      }

      // Load recent face events for this person
      const getEventsFn = httpsCallable(functions, 'getFaceEvents')
      const eventsResult = await getEventsFn({ groupId, personId, limit: 8 })
      const eventsData = eventsResult.data as { success: boolean; events: FaceEvent[] }

      if (eventsData.success) {
        setRecentEvents(eventsData.events)
      }
    } catch (err) {
      console.error('Failed to load person data:', err)
      setError(t('personDetail.failedToLoad'))
    } finally {
      setLoading(false)
    }
  }, [personId, groupId, initialPerson])

  useEffect(() => {
    if (open && personId) {
      loadData()
    } else {
      // Reset state when dialog closes
      setPerson(null)
      setRecentEvents([])
      setName('')
      setClassificationId('')
      setNotes('')
      setExpiresAt('')
      setError(null)
    }
  }, [open, personId, loadData])

  const handleSave = async () => {
    if (!person) return

    setSaving(true)
    try {
      const updateFn = httpsCallable(functions, 'updatePerson')
      const payload: Record<string, any> = {
        groupId,
        personId: person.id,
      }

      // Only send fields the user has permission to change
      if (canManagePersons) {
        payload.name = name || null
        payload.notes = notes || null
        payload.expiresAt = expiresAt || null
        payload.classificationId = classificationId
      }

      await updateFn(payload)

      onUpdated()
      onOpenChange(false)
    } catch (err) {
      console.error('Failed to save person:', err)
      setError(t('personDetail.failedToSave'))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!person) return

    setDeleting(true)
    try {
      const deleteFn = httpsCallable(functions, 'deletePerson')
      await deleteFn({ groupId, personId: person.id })

      onDeleted()
      onOpenChange(false)
    } catch (err) {
      console.error('Failed to delete person:', err)
      setError(t('personDetail.failedToDelete'))
    } finally {
      setDeleting(false)
    }
  }

  const handleArchive = async () => {
    if (!person) return

    setArchiving(true)
    try {
      const updateFn = httpsCallable(functions, 'updatePerson')
      await updateFn({ groupId, personId: person.id, archivedAt: new Date().toISOString() })

      onDeleted()
      onOpenChange(false)
    } catch (err) {
      console.error('Failed to archive person:', err)
      setError(t('personDetail.failedToArchive'))
    } finally {
      setArchiving(false)
    }
  }

  const handleRestore = async () => {
    if (!person) return

    setArchiving(true)
    try {
      const updateFn = httpsCallable(functions, 'updatePerson')
      await updateFn({ groupId, personId: person.id, archivedAt: null })

      onUpdated()
      onOpenChange(false)
    } catch (err) {
      console.error('Failed to restore person:', err)
      setError(t('personDetail.failedToRestore'))
    } finally {
      setArchiving(false)
    }
  }

  if (!personId) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('personDetail.title')}</DialogTitle>
          <DialogDescription>
            {t('personDetail.description')}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : error && !person ? (
          <div className="py-8">
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          </div>
        ) : person ? (
          <div className="space-y-6 py-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Primary face and stats */}
            <div className="flex gap-4">
              <div className="w-24 h-24 rounded-lg overflow-hidden border bg-paper-2">
                {person.thumbnailUrl ? (
                  <img
                    src={person.thumbnailUrl}
                    alt={person.name || t('common.unknown')}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                    <User className="h-8 w-8" />
                  </div>
                )}
              </div>
              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Eye className="h-4 w-4" />
                  {t('personDetail.totalSightings', { count: person.totalSightings })}
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  {t('personDetail.firstSeen', { date: formatDateTime(person.firstSeenAt) })}
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  {t('personDetail.lastSeen', { date: formatDateTime(person.lastSeenAt) })}
                </div>
                {person.expiresAt && (
                  <div className={`flex items-center gap-2 text-sm ${new Date(person.expiresAt) < new Date() ? 'text-[#7a3325]' : 'text-muted-foreground'}`}>
                    <Clock className="h-4 w-4" />
                    {new Date(person.expiresAt) < new Date() ? t('personDetail.expired') : t('personDetail.expires')}: {new Date(person.expiresAt).toLocaleDateString()}
                  </div>
                )}
              </div>
            </div>

            {/* View only notice */}
            {!canManagePersons && (
              <Alert>
                <EyeOff className="h-4 w-4" />
                <AlertDescription>
                  {t('personDetail.viewOnlyNotice')}
                </AlertDescription>
              </Alert>
            )}

            {/* Form fields */}
            <div className="space-y-4">
              {/* Name */}
              <div className="space-y-2">
                {canManagePersons ? (
                  <>
                    <Label htmlFor="edit-name">{t('personDetail.name')}</Label>
                    <Input
                      id="edit-name"
                      placeholder={t('personDetail.namePlaceholder')}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </>
                ) : (
                  <>
                    <Label>{t('personDetail.name')}</Label>
                    <p className="text-sm text-foreground">{name || t('common.unknown')}</p>
                  </>
                )}
              </div>

              {/* Classification */}
              <div className="space-y-2">
                {canManagePersons ? (
                  <>
                    <Label htmlFor="edit-classification">{t('personDetail.classification')}</Label>
                    <Select
                      value={classificationId}
                      onValueChange={setClassificationId}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('personDetail.selectClassification')} />
                      </SelectTrigger>
                      <SelectContent>
                        {classifications.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            <div className="flex items-center gap-2">
                              <div
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: c.color }}
                              />
                              {c.displayName}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </>
                ) : (
                  <>
                    <Label>{t('personDetail.classification')}</Label>
                    <div className="flex items-center gap-2">
                      {person?.classificationColor && (
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: person.classificationColor }}
                        />
                      )}
                      <span className="text-sm">{person?.classificationName || t('common.unknown')}</span>
                    </div>
                  </>
                )}
              </div>

              {/* Notes */}
              <div className="space-y-2">
                {canManagePersons ? (
                  <>
                    <Label htmlFor="edit-notes">{t('personDetail.notes')}</Label>
                    <Textarea
                      id="edit-notes"
                      placeholder={t('personDetail.notesPlaceholder')}
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={3}
                    />
                  </>
                ) : notes ? (
                  <>
                    <Label>{t('personDetail.notes')}</Label>
                    <p className="text-sm text-muted-foreground">{notes}</p>
                  </>
                ) : null}
              </div>

              {/* Expiration Date */}
              <div className="space-y-2">
                {canManagePersons ? (
                  <>
                    <Label htmlFor="edit-expires">{t('personDetail.expirationDate')}</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id="edit-expires"
                        type="date"
                        value={expiresAt}
                        onChange={(e) => setExpiresAt(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        className="flex-1"
                      />
                      {expiresAt && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setExpiresAt('')}
                          className="text-muted-foreground"
                        >
                          {t('common.remove')}
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {t('personDetail.expirationHint')}
                    </p>
                  </>
                ) : expiresAt ? (
                  <>
                    <Label>{t('personDetail.expirationDate')}</Label>
                    <div className="flex items-center gap-2 text-sm">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <span>{new Date(expiresAt).toLocaleDateString()}</span>
                    </div>
                  </>
                ) : null}
              </div>
            </div>

            {/* Recent events */}
            {recentEvents.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>{t('personDetail.recentSightings')}</Label>
                  {personId && (
                    <Button
                      variant="link"
                      size="sm"
                      className="text-xs p-0 h-auto"
                      onClick={() => {
                        navigate(`/sightings?person=${personId}&personName=${encodeURIComponent(person?.name || 'Unknown')}`)
                      }}
                    >
                      {t('personDetail.viewAllSightings')}
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {recentEvents.map((event) => (
                    <div
                      key={event.id}
                      className="relative group rounded-lg overflow-hidden border bg-paper-2 cursor-pointer hover:ring-2 hover:ring-primary transition-all"
                      onClick={() => {
                        if (personId) {
                          const query = `?person=${personId}&personName=${encodeURIComponent(person?.name || 'Unknown')}`
                          if (event.sightingId) {
                            navigate(`/sightings/${event.sightingId}${query}`)
                          } else {
                            navigate(`/sightings${query}`)
                          }
                        }
                      }}
                    >
                      <div className="aspect-square">
                        {event.thumbnailUrl ? (
                          <img
                            src={event.thumbnailUrl}
                            alt="Face"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <User className="h-6 w-6 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-1.5 py-0.5">
                        <span className="text-[11px] text-white">
                          {formatRelativeTime(event.timestamp, '')}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Archived status indicator */}
            {person.archivedAt && (
              <Alert variant="warning">
                <Archive className="h-4 w-4" />
                <AlertDescription>{t('personDetail.archived')}: {new Date(person.archivedAt).toLocaleDateString()}</AlertDescription>
              </Alert>
            )}

            {/* Actions - only shown if user can manage */}
            {canManagePersons && (
              <div className="pt-4 border-t">
                {isArchiveView && isAdmin ? (
                  /* Archive view: Restore + Delete buttons */
                  <div className="flex gap-2 sm:flex-col sm:space-y-2 sm:gap-0">
                    {person.archivedAt && (
                      <Button
                        variant="outline"
                        className="flex-1 sm:w-full"
                        onClick={handleRestore}
                        disabled={archiving}
                      >
                        <RotateCcw className="h-4 w-4 mr-2" />
                        {archiving ? t('common.saving') : t('personDetail.restorePerson')}
                      </Button>
                    )}
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="destructive" className="flex-1 sm:w-full">
                          <Trash2 className="h-4 w-4 mr-2" />
                          <span className="hidden sm:inline">{t('personDetail.deletePerson')}</span>
                          <span className="sm:hidden">{t('common.delete')}</span>
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>{t('personDetail.deleteConfirmTitle')}</AlertDialogTitle>
                          <AlertDialogDescription>
                            {t('personDetail.deleteConfirmDescription')}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={handleDelete}
                            disabled={deleting}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            {deleting ? t('common.deleting') : t('common.delete')}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                ) : (
                  /* Normal view: Merge + Archive buttons */
                  <div className="flex gap-2 sm:flex-col sm:space-y-2 sm:gap-0">
                    <Button
                      variant="outline"
                      className="flex-1 sm:w-full"
                      onClick={() => setMergeDialogOpen(true)}
                    >
                      <Merge className="h-4 w-4 mr-2" />
                      <span className="hidden sm:inline">{t('personDetail.mergeInto')}</span>
                      <span className="sm:hidden">{t('personDetail.merge')}</span>
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" className="flex-1 sm:w-full text-orange-600 border-orange-300 hover:bg-orange-50">
                          <Archive className="h-4 w-4 mr-2" />
                          <span className="hidden sm:inline">{t('personDetail.archivePerson')}</span>
                          <span className="sm:hidden">{t('personDetail.archive')}</span>
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>{t('personDetail.archiveConfirmTitle')}</AlertDialogTitle>
                          <AlertDialogDescription>
                            {t('personDetail.archiveConfirmDescription')}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={handleArchive}
                            disabled={archiving}
                          >
                            {archiving ? t('common.saving') : t('personDetail.archivePerson')}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                )}

                {!isArchiveView && (
                  <MergePersonDialog
                    open={mergeDialogOpen}
                    onOpenChange={setMergeDialogOpen}
                    sourcePerson={person}
                    groupId={groupId}
                    onMerged={() => {
                      onDeleted()
                      onOpenChange(false)
                    }}
                  />
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="py-8 text-center text-muted-foreground">
            {t('persons.personNotFound')}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="hidden sm:inline-flex">
            {canManagePersons ? t('common.cancel') : t('common.close')}
          </Button>
          {canManagePersons && (
            <Button onClick={handleSave} disabled={saving || loading || !person}>
              {saving ? t('common.saving') : t('common.saveChanges')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
